import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { Button } from '../ui/button';

// Reusable server-side pagination bar.
// Props: page (1-based), pageSize, total, onPageChange(newPage)
export const Pagination = ({ page, pageSize, total, onPageChange, className = '' }) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total || 0);
  const go = (p) => { if (p >= 1 && p <= totalPages && p !== page) onPageChange(p); };

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border ${className}`} data-testid="pagination-bar">
      <p className="text-sm text-muted-foreground" data-testid="pagination-info">
        Showing <span className="font-medium text-foreground">{start}</span>–<span className="font-medium text-foreground">{end}</span> of <span className="font-medium text-foreground">{total || 0}</span>
      </p>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(1)} disabled={page <= 1} data-testid="pagination-first" aria-label="First page"><ChevronsLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(page - 1)} disabled={page <= 1} data-testid="pagination-prev" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="px-3 text-sm tabular-nums" data-testid="pagination-page">{page} / {totalPages}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(page + 1)} disabled={page >= totalPages} data-testid="pagination-next" aria-label="Next page"><ChevronRight className="w-4 h-4" /></Button>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => go(totalPages)} disabled={page >= totalPages} data-testid="pagination-last" aria-label="Last page"><ChevronsRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

// Sortable table header cell. Clicking toggles asc/desc for its field.
// Props: field, label, sortBy, sortDir, onSort(field), align ('left'|'right')
export const SortableTH = ({ field, label, sortBy, sortDir, onSort, align = 'left' }) => {
  const active = sortBy === field;
  const Icon = !active ? ChevronsUpDown : (sortDir === 'desc' ? ArrowDown : ArrowUp);
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-foreground' : ''} ${align === 'right' ? 'flex-row-reverse' : ''}`}
        data-testid={`sort-${field}`}
      >
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};
